/**
 * Search application – mounts on `[data-component="search"]`.
 */
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import FunnelbackSearchForm, {
  buildLookupSearchUrl,
} from '../funnelback-search-form';
import FacetFilters from '../facet-filters';
import SelectedFacetChips from '../selected-facet-chips';
import SearchResults from '../search-results';
import SearchPaginationBar from '../search-pagination-bar';
import { getMetadataFieldValue } from '../../utils';
import { buildSafeFunnelbackSuggestFetchUrl } from '../../utils/funnelback-fetch-url';
import {
  SearchAppContext,
  SearchAppDispatchContext,
} from '../../context/search-app/search-app-context';
import { SET_QUERY } from '../../context/search-app/search-app-actions';
import { useSearchAppQuery } from '../../context/search-app/use-search-app-query';
import { mergeLookupExtrasWithDefaults } from '../../context/search-app/search-url';

export const BASE_CLASS = 'search-app';

const Search = () => {
  const state = useContext(SearchAppContext);
  const { dispatch } = useContext(SearchAppDispatchContext);
  const searchRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState('Find what you are after');
  useSearchAppQuery(state, dispatch);

  useEffect(() => {
    const searchTitle = getMetadataFieldValue('searchTitle');
    if (searchTitle) {
      setTitle(searchTitle);
    }
  }, []);

  const getAutocompleteUrl = useCallback(
    (partialQuery: string) => {
      const base = state.suggestBaseUrl.trim();
      if (!base) {
        return '';
      }
      return (
        buildSafeFunnelbackSuggestFetchUrl(
          base,
          partialQuery,
          state.suggestExtras,
        ) ?? ''
      );
    },
    [state.suggestBaseUrl, state.suggestExtras],
  );

  const buildSearchUrl = useCallback(
    (query: string) =>
      buildLookupSearchUrl(
        state.lookupBaseUrl,
        query,
        mergeLookupExtrasWithDefaults(state.lookupExtras),
      ),
    [state.lookupBaseUrl, state.lookupExtras],
  );

  const onQueryChange = useCallback(
    (query: string) => {
      dispatch({ type: SET_QUERY, payload: query });
    },
    [dispatch],
  );

  const onSearchCommit = useCallback(
    (query: string) => {
      dispatch({ type: SET_QUERY, payload: query });
    },
    [dispatch],
  );

  return (
    <div className={BASE_CLASS} ref={searchRef}>
      <div className={`${BASE_CLASS}__wrapper`}>
        <h2 className={`${BASE_CLASS}__title`}>{title}</h2>
        <div className="filter-toolbar mb-2">
          {state.showTextSearch ? (
            <FunnelbackSearchForm
              baseClassName="search-form"
              autocompleteUrlBase={state.suggestBaseUrl.trim()}
              getAutocompleteUrl={getAutocompleteUrl}
              buildSearchUrl={buildSearchUrl}
              placeholder="Type to search"
              labelText="Search Deakin Life"
              controlledQuery={state.query}
              onQueryChange={onQueryChange}
              onSearchCommit={onSearchCommit}
              allowEmptySubmit
            />
          ) : null}
          {state.showFacets ? (
            <div className="filter-toolbar__controls">
              <FacetFilters />
            </div>
          ) : null}
        </div>
        {state.showFacets ? <SelectedFacetChips /> : null}
        <SearchPaginationBar searchRef={searchRef} position="before" />
        <SearchResults />
        <SearchPaginationBar searchRef={searchRef} position="after" />
      </div>
    </div>
  );
};

export default Search;
